/*
 * Created on Wed Oct 11 2023 4:56:55 PM
 *
 * ***************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2023 
 *
 * All Rights Reserved
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ****************************************************************************************
 */
import { useState, useEffect } from 'react';
import { getCleanProductName, getCategoryName, getOrderSpecialItemFlags } from '../utils/orderUtils';

const useOrderFilters = (orders) => {
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [selectedFilter, setSelectedFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('newest');

    useEffect(() => {
        if (!Array.isArray(orders) || orders.length === 0) {
            setFilteredOrders([]);
            return;
        }

        let filtered = [...orders];

        // Apply status filter
        if (selectedFilter === 'paid') {
            filtered = orders.filter(ord => (ord?.currentStatus || ord?.status) === 'PAID');
        } else if (selectedFilter === 'payment_pending') {
            filtered = orders.filter(ord =>
                ['PAYMENT_PENDING', 'PAYMENT_PROCESSING'].includes(ord?.currentStatus || ord?.status)
            );
        } else if (selectedFilter === 'refund_pending') {
            filtered = orders.filter(ord => (ord?.currentStatus || ord?.status) === 'REFUND_PENDING');
        } else if (selectedFilter === 'cancelled') {
            filtered = orders.filter(ord => (ord?.currentStatus || ord?.status) === 'CANCELLED');
        } else if (selectedFilter === 'has_add_on') {
            filtered = orders.filter((ord) => getOrderSpecialItemFlags(ord).hasAddOnItems);
        } else if (selectedFilter === 'has_late_fee') {
            filtered = orders.filter((ord) => getOrderSpecialItemFlags(ord).hasLateFeeItems);
        }

        // Apply search
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(ord => {
                const productName = getCleanProductName(ord);
                const categoryName = getCategoryName(ord);

                return (
                    ord.number?.toString().toLowerCase().includes(term) ||
                    ord.memo?.toLowerCase().includes(term) ||
                    (ord?.currentStatus || ord?.status)?.toLowerCase().includes(term) ||
                    productName.toLowerCase().includes(term) ||
                    categoryName.toLowerCase().includes(term)
                );
            });
        }

        // Apply sorting
        if (sortBy === 'newest') {
            filtered.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
        } else if (sortBy === 'oldest') {
            filtered.sort((a, b) => new Date(a.createdDate) - new Date(b.createdDate));
        } else if (sortBy === 'amount-high') {
            filtered.sort((a, b) => (b.totalBeforTax || 0) - (a.totalBeforTax || 0));
        } else if (sortBy === 'amount-low') {
            filtered.sort((a, b) => (a.totalBeforTax || 0) - (b.totalBeforTax || 0));
        }

        setFilteredOrders(filtered);
    }, [orders, selectedFilter, searchTerm, sortBy]);

    return {
        filteredOrders,
        selectedFilter,
        setSelectedFilter,
        searchTerm,
        setSearchTerm,
        sortBy,
        setSortBy
    };
};

export default useOrderFilters;
